import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage, TOptions } from '../i18n/LanguageContext';
import { FaSearch } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { FaGoogle } from 'react-icons/fa';
import { FaUser, FaSignOutAlt, FaCreditCard } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  img {
    width: 24px;
    height: 24px;
  }
`;

const LogoText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #000;
  text-decoration: none;
  letter-spacing: -0.5px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const NavLink = styled(Link)`
  color: #4A5568;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #2D3748;
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 4px;
  color: #3c4043;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  svg {
    font-size: 18px;
    color: #4285f4;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 160px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const UserWelcome = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  color: #333;
  
  .welcome {
    color: #666;
    font-size: 12px;
    margin-bottom: 2px;
  }
  
  .name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 20px;
    height: 20px;
    color: #666;
  }
  
  &.fallback {
    svg {
      display: block;
    }
  }
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  svg {
    font-size: 16px;
    color: #666;
  }
  
  &:last-child {
    border-top: 1px solid #eee;
  }
`;

const Navbar: React.FC = () => {
  const { t } = useLanguage();
  const { user, signInWithGoogle, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const [avatarError, setAvatarError] = useState(false);
  
  // 当路径改变时关闭下拉菜单
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);
  
  // 点击页面其他地方时关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDropdownOpen && !target.closest('.user-menu')) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  // 当用户变化时重置头像错误状态
  useEffect(() => {
    if (user) {
      setAvatarError(false);
    }
  }, [user]);
  
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      // 如果不在主页，先跳转到主页
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
  };
  
  return (
    <Nav>
      <NavContainer>
        <Logo to="/">
          <img src="/icon.png" alt="SheetMate Logo" />
          <LogoText>SheetMate</LogoText>
        </Logo>
        <NavLinks>
          <NavLink 
            to="/"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname !== '/') {
                window.location.href = '/';
              } else {
                scrollToSection('hero');
              }
            }}
          >
            {t('navigation.home')}
          </NavLink>
          <NavLink 
            to="/"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('features');
            }}
          >
            {t('navigation.features')}
          </NavLink>
          <NavLink 
            to="/"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('faq');
            }}
          >
            {t('navigation.faq')}
          </NavLink>
          <NavLink to="/pricing">{t('navigation.pricing')}</NavLink>
          {user ? (
            <UserMenu className="user-menu">
              <UserAvatar 
                className={(!user.photoURL || avatarError) ? 'fallback' : ''}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                {user.photoURL && !avatarError ? (
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || 'User'} 
                    onError={() => setAvatarError(true)}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <FaUser />
                )}
              </UserAvatar>
              <DropdownMenu isOpen={isDropdownOpen}>
                <UserWelcome>
                  <div className="welcome">{t('navigation.welcome')}</div>
                  <div className="name">{user.displayName || user.email}</div>
                </UserWelcome>
                <Link to="/mySubscription" style={{ textDecoration: 'none' }}>
                  <DropdownItem onClick={() => setIsDropdownOpen(false)}>
                    <FaCreditCard />
                    {t('navigation.mySubscription')}
                  </DropdownItem>
                </Link>
                <DropdownItem onClick={handleLogout}>
                  <FaSignOutAlt />
                  {t('navigation.signOut')}
                </DropdownItem>
              </DropdownMenu>
            </UserMenu>
          ) : (
            <GoogleButton onClick={signInWithGoogle}>
              <FaGoogle />
              {t('navigation.signInWithGoogle')}
            </GoogleButton>
          )}
        </NavLinks>
      </NavContainer>
    </Nav>
  );
};

export default Navbar; 